import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { getCategory, updateCategory } from 'redux/actions/categories'
import Page from 'components/Page'
import CategoryForm from 'components/forms/CategoryForm'
import { useMutation } from '@tanstack/react-query'
import { updateFile, uploadBulkFiles } from '../../../api/files'
import { message } from 'antd'
import { notify } from 'reapop'

const CategoriesView = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const { item: category, isGetLoading, isChangeLoading } = useSelector((state) => state.categories)

  const updateCategoryImageMutation = useMutation(updateFile, {
    onSuccess: async () => {
      message.success('Category image successfully updated')
    },
    onError: (err) => {
      if (err.response?.data?.messages.length) {
        dispatch(
          notify({ title: 'Category image upload failed', status: 'error', dismissAfter: 4000 }),
        )
      } else {
        return message.error('Something went wrong')
      }
    },
  })

  const updateHomeImageMutation = useMutation(updateFile, {
    onSuccess: async () => {
      message.success('Home image successfully updated')
    },
    onError: (err) => {
      if (err.response?.data?.messages.length) {
        dispatch(
          notify({ title: 'Home image upload failed', status: 'error', dismissAfter: 4000 }),
        )
      } else {
        return message.error('Something went wrong')
      }
    },
  })

  const uploadHomeImageMutation = useMutation(
    ({ file, onProgress }) => uploadBulkFiles([file], { onProgress }),
    {
      onSuccess: async () => {
        message.success('Home image successfully uploaded')
      },
      onError: (err) => {
        if (err.response?.data?.messages.length) {
          dispatch(
            notify({ title: 'Home image upload failed', status: 'error', dismissAfter: 4000 }),
          )
        } else {
          return message.error('Something went wrong')
        }
      },
    },
  )

  useEffect(() => {
    dispatch(getCategory({ id }))
  }, [id])

  const submitHandler = async (id, { name, arabianName, isVisible, isHomeVisible, categoryImage, homeImage }) => {
    if (categoryImage) {
      await updateCategoryImageMutation.mutateAsync({
        id: categoryImage.id,
        file: categoryImage.file,
      })
    }

    let newHomeImageId = null
    if (homeImage) {
      if (homeImage.id) {
        // Update existing home image
        await updateHomeImageMutation.mutateAsync({
          id: homeImage.id,
          file: homeImage.file,
        })
      } else {
        // Upload new home image
        const progressKey = 'category-home-image-upload-progress'
        try {
          const uploadedFiles = await uploadHomeImageMutation.mutateAsync({
            file: homeImage.file,
            onProgress: (percent) => {
              message.open({
                key: progressKey,
                type: 'loading',
                duration: 0,
                content: `Uploading image... ${percent}%`,
              })
            },
          })
          message.destroy(progressKey)
          newHomeImageId = uploadedFiles[0]?.id
        } catch (error) {
          message.destroy(progressKey)
          return
        }
      }
    }

    dispatch(
      updateCategory({
        id,
        data: {
          name,
          arabianName,
          isVisible,
          isHomeVisible,
          ...(newHomeImageId && { homeImage: { id: newHomeImageId } }),
        },
        navigate,
      }),
    )
  }

  return (
    <Page pageTitle={category.name} isGetLoading={isGetLoading} isChangeLoading={isChangeLoading}>
      <CategoryForm
        defaultValues={category}
        submitHandler={(data) => submitHandler(id, data)}
        loading={isChangeLoading}
      />
    </Page>
  )
}

export default CategoriesView
